import { setUiLocalePreference, t } from '@web/i18n';
import { isUiLocalePreference, UI_LOCALE_PREFERENCES } from '@web/i18n/locale';
import { useUiLocalePreference } from '@web/i18n/locale_store';
import {
  AppIcons,
  Avatar,
  AvatarFallback,
  Button,
  Card,
  PageSectionHead,
  Savebar,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@web/ui/core';
import { FormTextField } from '@web/ui/core/molecules/form';

import { diffPatch, useProfilePage } from '@web/ui/settings/hooks/use_profile_page';
import { fireAndForget } from '@web/ui/core/utils';

const LOCALE_OPTION_KEY = {
  auto: 'profile.locale.option.auto',
  'pt-BR': 'profile.locale.option.pt-BR',
  en: 'profile.locale.option.en',
} as const;

/** Preferência local (localStorage): aplica na hora, não entra no PATCH /me. */
function InterfaceLanguageCard(): React.JSX.Element {
  const preference = useUiLocalePreference((s) => s.preference);
  return (
    <Card>
      <div className="p-5">
        <header className="mb-4">
          <h2 className="m-0 text-meta font-medium text-foreground">{t('profile.locale.title')}</h2>
          <p className="m-0 mt-1 text-meta text-muted-foreground">{t('profile.locale.sub')}</p>
        </header>
        <Select
          value={preference}
          onValueChange={(value) => {
            if (isUiLocalePreference(value)) fireAndForget(setUiLocalePreference(value));
          }}
        >
          <SelectTrigger className="max-w-xs" aria-label={t('profile.locale.label')}>
            <SelectValue />
          </SelectTrigger>
          {/* item-aligned: sem medição FloatingUI, dirigível em jsdom (mesmo motivo do upload_form). */}
          <SelectContent position="item-aligned">
            {UI_LOCALE_PREFERENCES.map((option) => (
              <SelectItem key={option} value={option}>
                {t(LOCALE_OPTION_KEY[option])}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </Card>
  );
}

export default function ProfilePage(): React.JSX.Element | null {
  const ctrl = useProfilePage();
  if (!ctrl.isReady || !ctrl.me) return null;
  const { form, initialValues, me, isSaving, discard, save } = ctrl;

  return (
    <>
      <PageSectionHead title={t('profile.title')} sub={t('profile.sub')} />

      <div className="flex flex-col gap-5">
        <Card>
          <div className="p-5">
            <div className="mb-5 flex items-center gap-4">
              <span className="relative inline-flex">
                <Avatar size="xl" tone="brand">
                  <AvatarFallback displayName={me.displayName || me.name || me.email} />
                </Avatar>
                <span
                  aria-hidden
                  className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-pill border-2 border-surface bg-tone-green-fg"
                />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-meta text-muted-foreground">
                  <span className="truncate text-foreground">{me.email}</span>
                  <span aria-hidden className="text-faint-foreground">
                    ·
                  </span>
                  <span className="inline-flex items-center gap-1 text-foreground-default">
                    <AppIcons name="shield-check" size={12} />
                    {t('profile.identity.sso')}
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled
                title={t('profile.identity.photoComingSoon')}
              >
                <AppIcons name="camera" size={13} />
                {t('profile.identity.changePhoto')}
              </Button>
            </div>

            <form.Field
              name="displayName"
              validators={{
                onChange: ({ value }) =>
                  value.trim().length === 0 || value.trim().length > 80
                    ? t('profile.field.displayName.error')
                    : undefined,
              }}
            >
              {(field) => (
                <FormTextField
                  field={field}
                  label={t('profile.field.displayName.label')}
                  placeholder={t('profile.field.displayName.placeholder')}
                  hint={t('profile.field.displayName.hint')}
                />
              )}
            </form.Field>
          </div>
        </Card>

        <Card>
          <div className="p-5">
            <header className="mb-4">
              <h2 className="m-0 text-meta font-medium text-foreground">
                {t('profile.contact.title')}
              </h2>
              <p className="m-0 mt-1 text-meta text-muted-foreground">{t('profile.contact.sub')}</p>
            </header>

            <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
              <form.Field name="phone">
                {(field) => (
                  <FormTextField
                    field={field}
                    label={t('profile.field.phone.label')}
                    placeholder={t('profile.field.phone.placeholder')}
                    startAdornment={
                      <span className="inline-flex items-center gap-1.5">
                        <AppIcons name="device-mobile" size={14} />
                        {t('profile.field.phone.dialCode')}
                      </span>
                    }
                  />
                )}
              </form.Field>
              <form.Field name="whatsapp">
                {(field) => (
                  <FormTextField
                    field={field}
                    label={t('profile.field.whatsapp.label')}
                    placeholder={t('profile.field.whatsapp.placeholder')}
                    startAdornment={
                      <span className="inline-flex items-center gap-1.5">
                        <AppIcons name="whatsapp" size={14} />
                        {t('profile.field.phone.dialCode')}
                      </span>
                    }
                  />
                )}
              </form.Field>
            </div>
          </div>
        </Card>

        <InterfaceLanguageCard />
      </div>

      <form.Subscribe selector={(s) => s.values}>
        {(values) => {
          const dirty = Object.keys(diffPatch(initialValues, values)).length > 0;
          return <Savebar open={dirty} disabled={isSaving} onDiscard={discard} onSave={save} />;
        }}
      </form.Subscribe>
    </>
  );
}
